import { useHistory } from 'umi';
import { useCallback, useEffect, useState, useRef } from 'react';
import { defaultFilterValue } from '../constants';
import {
  dealwithChannelToBack,
  dealwithSessionData,
  getFilterName,
  getFilterValueStorageKey,
} from '../utils/storage';
import { getStatsDetailParams } from '@/pages/opinion/utils/storageUtils';
import { getShortUrlParams, getUrlParams } from '../utils';

export default function useDefaultFilter({
  defaultValue,
  hasServerParams,
  gameInfo,
  finallyDealwithDefaultValue = filter => filter,
  shortid,
  inheritStorageFilterValue = true,
}: {
  defaultValue: any;
  hasServerParams: boolean;
  gameInfo: any;
  userInfo: any;
  shortid?: string;
  inheritStorageFilterValue?: boolean;
  finallyDealwithDefaultValue?: (
    filter: any,
    otherPageParams?: any,
    isDefaultValue?: boolean,
  ) => any | Promise<any>;
}) {
  const [loading, setLoading] = useState(true);
  // 继承筛选器后的默认值
  const defaultFilter = useRef<{ [key: string]: any }>({});
  // 原始的默认值「不继承筛选器」
  const oriDefaultFilter = useRef<{ [key: string]: any }>({});
  const history = useHistory();

  // 这个是其他页面跳转过来所传递的参数
  const otherPageParams = useCallback(() => {
    const data = getStatsDetailParams();
    if (data) {
      const { statsDetailType, ...otherProps } = data;
      const params = dealwithChannelToBack(otherProps);
      return {
        ...otherProps,
        ...params,
      };
    }
    return null;
  }, []);

  const sessionStorageParams = useCallback(() => {
    if (!inheritStorageFilterValue) {
      return { data: {}, isToday: true };
    }
    const key = getFilterValueStorageKey(gameInfo);
    const data = JSON.parse(sessionStorage.getItem(key) || '{}');

    return dealwithSessionData(data);
  }, [gameInfo, inheritStorageFilterValue]);

  const getSessionStorageFilterName = useCallback(() => {
    const key = getFilterName(gameInfo);
    return !key;
  }, [gameInfo]);

  // 如果有默认时间，并且缓存时间为当前日期，那么就使用用户设置的时间
  const dealwithSessionAndUserInfo = (
    defaultValue: any,
    sessionInfo: any,
    isToday: boolean,
  ) => {
    if (isToday && getSessionStorageFilterName())
      return { ...sessionInfo, ...defaultValue };

    const { start_time, end_time, date_type, ...otherInfo } =
      defaultValue || {};

    return { ...sessionInfo, ...otherInfo };
  };

  function hasValue(value?: any) {
    if (!value) return false;
    if (Array.isArray(value)) return false;
    if (typeof value !== 'object') return false;
    if (Object.keys(value).length === 0) return false;

    return true;
  }

  const getDefaultFilterInfo = async () => {
    setLoading(true);
    // 页面默认值
    const defaultInfo = defaultFilterValue(defaultValue);

    // 优先级第一: 链接参数默认值
    const urlInfo = getUrlParams(history);
    // 优先级第二: 服务器短链接缓存默认值
    const shortServerFilter = await getShortUrlParams(
      history,
      shortid,
      defaultValue,
    );
    // 优先级第三: 本地缓存值和链接缓存值参数
    const { data, isToday } = sessionStorageParams(); // 本地缓存
    const userSetInfo = dealwithSessionAndUserInfo(defaultValue, data, isToday);
    const stateFilter = otherPageParams(); // 链接上本地缓存默认值stateDetailKey
    const storageFilterInfo = hasValue(stateFilter) ? stateFilter : userSetInfo;

    let filterInfo = { ...defaultInfo, ...storageFilterInfo };
    if (hasValue(urlInfo)) {
      filterInfo = { ...defaultInfo, ...urlInfo };
    } else if (hasValue(shortServerFilter)) {
      filterInfo = { ...defaultInfo, ...shortServerFilter };
    }

    const info = await finallyDealwithDefaultValue(filterInfo, stateFilter);

    const allDefaultInfo = await finallyDealwithDefaultValue(
      { ...defaultInfo },
      info,
      true,
    );
    oriDefaultFilter.current = allDefaultInfo;
    // 最后设置默认值
    defaultFilter.current = info;
    setLoading(false);
  };

  useEffect(() => {
    if (gameInfo.edition_unified_id) {
      getDefaultFilterInfo();
    }
  }, [gameInfo]);

  function backOriDefaultValue(value: any) {
    oriDefaultFilter.current = { ...oriDefaultFilter.current, ...value };
  }

  return {
    defaultFilter: defaultFilter.current,
    loading,
    oriDefaultFilter: oriDefaultFilter.current,
    backOriDefaultValue,
  };
}
